///
/// EarthBrowser
///
/// Created by Matt Giger on 10/31/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The "you are here" marker: the blue dot with a white ring the maps
/// apps use, over a translucent disc for the fix's accuracy radius and,
/// when the device has a compass, a cone pointing the way the phone
/// faces. Drawn as a screen-space pane (constant size at any zoom) that
/// is moved each frame to the projection of the fix, seated on the
/// terrain. The accuracy disc and the cone are projected on the ground,
/// so both foreshorten and turn with a tilted or rotated view.
///
/// Reads dialog.userLocation (services/userLocationService.ts). Hidden
/// while there is no fix, when the point is behind the limb or off
/// screen. New fixes are eased into place over the same time constant
/// FollowBehavior uses, so in follow mode the dot stays put at center.
///

import { PanelDepths } from "@/earthbrowser/constants/PanelDepths"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineBridge } from "@/earthbrowser/engineBridge"
import { geographic } from "@/earthbrowser/utils/geographic"
import type { Camera } from "@stovokor/camera/Camera"
import { animation, easing } from "@stovokor/math/animation"
import { rgba } from "@stovokor/math/color"
import { trig } from "@stovokor/math/math"
import { type matrix } from "@stovokor/math/matrix"
import { cross, dot, normal, vec2, vec3 } from "@stovokor/math/vector"
import { Canvas } from "@stovokor/uipanes/Canvas"
import { Pane } from "@stovokor/uipanes/Pane"

/** Pane edge (px); the accuracy disc is capped to fit inside it */
const cPaneSize = 240
const cHalf = cPaneSize / 2
/** Dot and ring radii (px) */
const cDotRadius = 6.5
const cRingRadius = 10
/** Heading cone length (px) and half-angle */
const cConeLength = 38
const cConeHalfAngle = 28 * trig.rad
/** Accuracy disc hidden below this radius (it would sit under the ring) */
const cMinAccuracyPx = cRingRadius + 4
/** Position easing time constant (s); matches FollowBehavior.cPositionTau */
const cPositionTau = 0.5
/** Heading easing time constant (s) */
const cHeadingTau = 0.18
/** Fade band on the cosine of the view incidence as the point nears the limb */
const cGrazingFade = 0.15
/** Keep updating a little past the viewport so the dot slides in rather than pops */
const cMarginPx = cHalf

const cBlue = { r: 0.16, g: 0.5, b: 1.0 }

export interface UserLocationMarkerStats {
	visible: boolean
	x: number
	y: number
	accuracyPx: number
	headingDeg: number | null
	/** Screen direction of the cone, degrees clockwise from screen-up; null without one */
	coneScreenDeg: number | null
	alpha: number
}

export class UserLocationMarker extends Pane {
	private content = new MarkerContent()
	private fadeIn = new animation().from(0).to(1).duration(0.25).easing(easing.cubicOut)
	private pulse = new animation().from(0).to(1).duration(1.1).easing(easing.cubicOut)
	private ebContext: EarthBrowserContext
	private shown = false
	private easedLat = 0
	private easedLon = 0
	private easedHeading = 0
	private headingKnown = false
	private lastTickMs = 0
	private screenX = 0
	private screenY = 0

	constructor(context: EarthBrowserContext) {
		super(context)
		this.ebContext = context
		this.depth = PanelDepths.CROSSHAIR
		this.visible = false
		this.size(cPaneSize, cPaneSize).contents(this.content)

		this.fadeIn.onUpdate(value => this.content.alpha = value)
		this.pulse.onUpdate(value => this.content.pulse = value)
			.onComplete(() => this.content.pulse = -1)
	}

	/** Per-frame: follow the fix on screen (EarthActivity frame task) */
	public update(camera: Camera): void {
		const now = performance.now()
		const dt = this.lastTickMs ? Math.min(0.1, (now - this.lastTickMs) / 1000) : 0
		this.lastTickMs = now

		const location = engineBridge.getState()?.dialog.userLocation
		const fix = location?.fix ?? null
		if (!fix) {
			this.hide()
			return
		}

		// First fix: appear with a pulse ring; later fixes glide over
		if (!this.shown) {
			this.shown = true
			this.easedLat = fix.lat
			this.easedLon = fix.lon
			this.headingKnown = false
			this.visible = true
			this.content.alpha = 0
			this.fadeIn.start()
			this.pulse.start()
		} else {
			const k = 1 - Math.exp(-dt / cPositionTau)
			this.easedLat += (fix.lat - this.easedLat) * k
			this.easedLon += shortestTurn(this.easedLon * trig.rad, fix.lon * trig.rad) * trig.deg * k
		}

		const headingDeg = location?.headingDeg ?? null
		if (headingDeg !== null) {
			const target = headingDeg * trig.rad
			if (!this.headingKnown) {
				this.easedHeading = target
				this.headingKnown = true
			} else {
				this.easedHeading += shortestTurn(this.easedHeading, target) * (1 - Math.exp(-dt / cHeadingTau))
			}
		}

		// Terrain-seated world point and its local east / north frame
		const flat = new geographic(this.easedLon * trig.rad, this.easedLat * trig.rad, 0)
		const geo = new geographic(flat.longitude, flat.latitude, this.ebContext.displayElevationKm(flat))
		const position = geo.cartesian
		const up = normal(position)
		const east = normal(cross(vec3.unitY, up))
		const north = cross(up, east)

		// Behind the limb (or edge-on), or behind the camera: nothing to draw
		const toCamera = camera.position.subtract(position)
		const incidence = dot(up, toCamera) / toCamera.length
		const viewAlign = dot(normal(toCamera.multiply(-1)), camera.state.forward)
		if (incidence <= 0.005 || viewAlign <= 0) {
			this.visible = false
			return
		}

		// Project through the live camera state rather than Camera.worldToScreen:
		// that uses the matrices of the last drawn frame (the viewport
		// rebuilds them after the frame tasks), which would put the dot a
		// frame behind the pose the director just wrote
		const projector = new ScreenProjector(camera)
		const screen = projector.project(position)
		if (!screen) {
			this.visible = false
			return
		}
		const width = camera.projection.width
		const height = camera.projection.height
		if (screen.x < -cMarginPx || screen.y < -cMarginPx || screen.x > width + cMarginPx || screen.y > height + cMarginPx) {
			this.visible = false
			return
		}
		this.visible = true
		this.screenX = screen.x
		this.screenY = screen.y
		this.offset(screen.x - cHalf, screen.y - cHalf)

		// Accuracy radius on screen: project the disc's eastern edge
		const accuracyKm = Math.max(0, fix.accuracyM) / 1000
		const edge = projector.project(position.add(east.multiply(accuracyKm)))
		const accuracyPx = edge ? Math.hypot(edge.x - screen.x, edge.y - screen.y) : 0

		// Heading cone direction: project a short step along the heading on
		// the ground so a tilted view foreshortens it correctly
		let coneAngle: number | null = null
		const probe = this.headingKnown ? projector.project(position.add(east.multiply(1))) : undefined
		if (probe) {
			const pxPerKm = Math.max(1e-6, Math.hypot(probe.x - screen.x, probe.y - screen.y))
			const stepKm = Math.min(5, cConeLength / pxPerKm)
			const dir = north.multiply(Math.cos(this.easedHeading)).add(east.multiply(Math.sin(this.easedHeading)))
			const tip = projector.project(position.add(dir.multiply(stepKm))) ?? screen
			const dx = tip.x - screen.x
			const dy = tip.y - screen.y
			if (Math.hypot(dx, dy) > 1e-3) {
				coneAngle = Math.atan2(dy, dx)
			}
		}

		this.content.grazing = Math.min(1, incidence / cGrazingFade)
		this.content.accuracyPx = accuracyPx
		this.content.coneAngle = coneAngle
	}

	private hide(): void {
		if (!this.shown) return
		this.shown = false
		this.fadeIn.stop()
		this.pulse.stop()
		this.content.pulse = -1
		this.visible = false
	}

	/** Dev-only diagnostics for window.__ebDebug.getUserLocation() */
	public get stats(): UserLocationMarkerStats {
		return {
			visible: this.visible,
			x: this.screenX,
			y: this.screenY,
			accuracyPx: this.content.accuracyPx,
			headingDeg: this.headingKnown ? ((this.easedHeading * trig.deg) % 360 + 360) % 360 : null,
			coneScreenDeg: this.content.coneScreenDeg,
			alpha: this.content.alpha,
		}
	}
}

function shortestTurn(from: number, to: number): number {
	return Math.atan2(Math.sin(to - from), Math.cos(to - from))
}

/** World-to-screen through the camera's current state (not its last drawn matrices) */
class ScreenProjector {
	private view: matrix
	private projection: matrix
	private width: number
	private height: number

	constructor(camera: Camera) {
		this.view = camera.state.lookToward
		this.projection = camera.projection.projection
		this.width = camera.projection.width
		this.height = camera.projection.height
	}

	/** CSS-pixel screen position, undefined behind the camera / degenerate */
	public project(v: vec3): vec2 | undefined {
		const eye = this.view.multVec4(v.x, v.y, v.z, 1)
		const clip = this.projection.multVec4(eye.x, eye.y, eye.z, eye.w)
		if (!(clip.w > 1e-9)) return undefined
		const x = (clip.x / clip.w + 1) * this.width / 2
		const y = (1 - clip.y / clip.w) * this.height / 2
		if (!Number.isFinite(x) || !Number.isFinite(y)) return undefined
		return new vec2(x, y)
	}
}

class MarkerContent extends Canvas {
	private _alpha = 0
	private _grazing = 1
	private _pulse = -1
	private _accuracyPx = 0
	private _coneAngle: number | null = null

	public get alpha(): number {
		return this._alpha
	}

	public set alpha(value: number) {
		if (this._alpha !== value) {
			this._alpha = value
			this.setNeedsUpdate()
		}
	}

	/** Fade as the point nears the limb */
	public set grazing(value: number) {
		if (Math.abs(this._grazing - value) > 0.01) {
			this._grazing = value
			this.setNeedsUpdate()
		}
	}

	/** Appearance pulse progress 0..1, -1 when idle */
	public set pulse(value: number) {
		if (this._pulse !== value) {
			this._pulse = value
			this.setNeedsUpdate()
		}
	}

	public get accuracyPx(): number {
		return this._accuracyPx
	}

	public set accuracyPx(value: number) {
		// Sub-pixel churn from camera motion shouldn't re-rasterize
		if (Math.abs(this._accuracyPx - value) > 0.5) {
			this._accuracyPx = value
			this.setNeedsUpdate()
		}
	}

	public get coneScreenDeg(): number | null {
		if (this._coneAngle === null) return null
		return ((this._coneAngle * trig.deg + 90) % 360 + 360) % 360
	}

	/** Screen angle (radians, canvas convention) the heading cone points, null for none */
	public set coneAngle(value: number | null) {
		if (value === null || this._coneAngle === null) {
			if (value !== this._coneAngle) {
				this._coneAngle = value
				this.setNeedsUpdate()
			}
		} else if (Math.abs(shortestTurn(this._coneAngle, value)) > 0.004) {
			this._coneAngle = value
			this.setNeedsUpdate()
		}
	}

	override render(): void {
		const a = this._alpha * this._grazing
		if (a <= 0) return
		const ctx = this.context
		const cx = cHalf
		const cy = cHalf

		// Accuracy disc, clipped to the pane (a disc bigger than the pane
		// would read as a square, so it is dropped past that)
		const maxDisc = cHalf - 2
		if (this._accuracyPx >= cMinAccuracyPx && this._accuracyPx <= maxDisc) {
			this.beginPath().circle(cx, cy, this._accuracyPx)
				.fill(rgba(cBlue.r, cBlue.g, cBlue.b, 0.14 * a))
			this.beginPath().circle(cx, cy, this._accuracyPx)
				.stroke(rgba(cBlue.r, cBlue.g, cBlue.b, 0.4 * a), 1)
		}

		// Appearance pulse: a ring expanding out of the dot and fading
		if (this._pulse >= 0) {
			const r = cRingRadius + (cHalf * 0.55 - cRingRadius) * this._pulse
			this.beginPath().circle(cx, cy, r)
				.stroke(rgba(cBlue.r, cBlue.g, cBlue.b, 0.6 * (1 - this._pulse) * a), 2)
		}

		// Heading cone: a wedge fading with distance from the dot
		if (this._coneAngle !== null) {
			const gradient = ctx.createRadialGradient(cx, cy, cRingRadius * 0.5, cx, cy, cConeLength)
			gradient.addColorStop(0, rgba(cBlue.r, cBlue.g, cBlue.b, 0.6 * a).toString())
			gradient.addColorStop(1, rgba(cBlue.r, cBlue.g, cBlue.b, 0).toString())
			ctx.beginPath()
			ctx.moveTo(cx, cy)
			ctx.arc(cx, cy, cConeLength, this._coneAngle - cConeHalfAngle, this._coneAngle + cConeHalfAngle)
			ctx.closePath()
			ctx.fillStyle = gradient
			ctx.fill()
		}

		// White ring with a soft shadow, then the blue dot
		ctx.save()
		this.shadow(rgba(0, 0, 0, 0.35 * a), 6, 0, 1)
		this.beginPath().circle(cx, cy, cRingRadius).fill(rgba(1, 1, 1, a))
		ctx.restore()
		this.beginPath().circle(cx, cy, cDotRadius).fill(rgba(cBlue.r, cBlue.g, cBlue.b, a))
	}
}
